import { useState, useEffect, useRef, useCallback } from 'react';

import { handleError } from 'utils/helper';

export const useSocket = ({ id_user, role, sender_name }) => {
  const [socket, setSocket] = useState(null);
  const [isConnected, setIsConnected] = useState(false);
  const [error, setError] = useState(null);
  const socketRef = useRef(null);
  const reconnectTimeoutRef = useRef(null);
  const reconnectAttemptsRef = useRef(0);
  const maxReconnectAttempts = 5; // MAX RECONNECT ATTEMPTS
  const reconnectIntervalRef = useRef([1000, 2000, 5000, 10000, 30000]); // RECONNECT INTERVAL

  const connectSocket = useCallback(() => {
    if (!id_user || !role) {
      return;
    }

    if (socketRef.current?.readyState === WebSocket.OPEN) {
      return;
    }

    // Clear reconnect timeout if there is one
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }

    if (socketRef.current && socketRef.current.readyState !== WebSocket.OPEN) {
      socketRef.current.close();
      socketRef.current = null;
    }

    const wsUrl = `${process.env.REACT_APP_SOCKET_URL}/ws/${id_user}?role=${role}`;

    try {
      const newSocket = new WebSocket(wsUrl);

      newSocket.onopen = () => {
        setIsConnected(true);
        setError(null);
        reconnectAttemptsRef.current = 0; // Reset reconnect attempts khi connect thành công
      };

      newSocket.onclose = (event) => {
        setIsConnected(false);

        // If not close code normal (1000, 1001) and not exceed max attempts
        if (event.code !== 1000 && event.code !== 1001 && reconnectAttemptsRef.current < maxReconnectAttempts) {
          // Auto reconnect with exponential backoff
          const delay =
            reconnectIntervalRef.current[reconnectAttemptsRef.current] || reconnectIntervalRef.current[reconnectIntervalRef.current.length - 1];

          reconnectTimeoutRef.current = setTimeout(() => {
            reconnectAttemptsRef.current += 1;
            connectSocket();
          }, delay);
        }
      };

      newSocket.onerror = (error) => {
        setError('WebSocket connection error');
        setIsConnected(false);
      };

      socketRef.current = newSocket;
      setSocket(newSocket);
    } catch (err) {
      setError('Failed to create WebSocket connection');
      setIsConnected(false);
    }
  }, [id_user, role]);

  const disconnectSocket = useCallback(() => {
    // Clear reconnect timeout
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }

    reconnectAttemptsRef.current = 0; // Reset attempts

    if (socketRef.current) {
      socketRef.current.close(1000, 'Manual disconnect'); // Close code 1000 = normal closure
      socketRef.current = null;
      setSocket(null);
      setIsConnected(false);
    }
  }, []);

  const sendMessage = useCallback(
    (event, message, conversation_id, receiver_id) => {
      const data = {
        event: event,
        sender_id: id_user,
        sender_type: role,
        sender_name: sender_name,
        conversation_id: conversation_id,
        receiver_id: receiver_id,
        content: message,
      };
      return new Promise((resolve, reject) => {
        if (!socketRef.current || socketRef.current.readyState !== WebSocket.OPEN) {
          reject(new Error('WebSocket is not connected'));
          return;
        }

        try {
          socketRef.current.send(JSON.stringify(data));
          resolve();
        } catch (error) {
          reject(error);
        }
      });
    },
    [id_user, role, sender_name]
  );

  const onMessage = useCallback((callback) => {
    if (!socketRef.current) return;

    socketRef.current.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);
        callback(data);
      } catch (error) {
        callback(event.data);
      }
    };
  }, []);

  useEffect(() => {
    // Heartbeat to check connection
    if (isConnected && socketRef.current?.readyState === WebSocket.OPEN) {
      const heartbeatInterval = setInterval(() => {
        if (socketRef.current?.readyState === WebSocket.OPEN) {
          // Send ping message (if server support)
          try {
            socketRef.current.send(JSON.stringify({ type: 'ping' }));
          } catch (error) {
            setIsConnected(false);
            handleError(error);
          }
        } else {
          setIsConnected(false);
        }
      }, 30000); // Ping every 30 seconds

      return () => clearInterval(heartbeatInterval);
    }
  }, [isConnected]);

  useEffect(() => {
    return () => {
      // Clear timeout when unmount
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
      }
      disconnectSocket();
    };
  }, [disconnectSocket]);

  return {
    socket,
    isConnected,
    error,
    connectSocket,
    disconnectSocket,
    sendMessage,
    onMessage,
  };
};
